import {
	DELIVERY_FILTERS,
	type DeliveryFilter,
	WEBHOOK_EVENTS,
} from "@repo/api/modules/webhooks/types";
import type { orpcClient } from "@shared/lib/orpc";
import { orpc } from "@shared/lib/orpc";
import { z } from "zod";

export const integrationsSearchSchema = z.object({
	/** Delivery log filtered to one endpoint (also the link in "endpoint turned off" notices). */
	endpoint: z.string().optional(),
	status: z.enum(DELIVERY_FILTERS).optional(),
	/** Delivery open in the detail sheet. */
	delivery: z.string().optional(),
});
export type IntegrationsSearch = z.infer<typeof integrationsSearchSchema>;

export type Endpoint = Awaited<
	ReturnType<typeof orpcClient.webhooks.endpoints.list>
>["endpoints"][number];
export type DeliveryRow = Awaited<
	ReturnType<typeof orpcClient.webhooks.deliveries.list>
>["deliveries"][number];
export type DeliveryStatus = DeliveryRow["status"];

export const DELIVERIES_PAGE = 25;

export function endpointsQuery(organizationId: string) {
	return orpc.webhooks.endpoints.list.queryOptions({
		input: { organizationId },
	});
}

export function deliveriesQuery(
	organizationId: string,
	search: Pick<IntegrationsSearch, "endpoint" | "status">,
) {
	return orpc.webhooks.deliveries.list.infiniteOptions({
		input: (cursor: string | undefined) => ({
			organizationId,
			limit: DELIVERIES_PAGE,
			...(search.endpoint ? { endpointId: search.endpoint } : {}),
			...(search.status ? { status: search.status } : {}),
			...(cursor ? { cursor } : {}),
		}),
		initialPageParam: undefined as string | undefined,
		getNextPageParam: (page) => page.nextCursor ?? undefined,
	});
}

/**
 * Deliveries have no live event (W1 publishes none per delivery), so a screen that shows one
 * in flight polls: fast while it is being sent, slowly while it waits for a retry, and the
 * 60 s heartbeat otherwise.
 */
export function pollInterval(statuses: (DeliveryStatus | undefined)[]) {
	if (statuses.includes("PENDING")) {
		return 2_000;
	}
	return statuses.includes("RETRYING") ? 30_000 : 60_000;
}

export const STATUS_BADGE: Record<
	DeliveryStatus,
	{ label: string; variant: "success" | "info" | "destructive" | "secondary" }
> = {
	SUCCEEDED: { label: "Succeeded", variant: "success" },
	PENDING: { label: "Sending", variant: "info" },
	RETRYING: { label: "Retrying", variant: "info" },
	DEAD: { label: "Dead", variant: "destructive" },
	CANCELLED: { label: "Cancelled", variant: "secondary" },
};

export const FILTER_LABEL: Record<DeliveryFilter, string> = {
	succeeded: "Succeeded",
	retrying: "Retrying",
	dead: "Dead",
	cancelled: "Cancelled",
};

const EVENT_LABEL = new Map<string, string>([
	...WEBHOOK_EVENTS.map((e) => [e.type, e.label] as const),
	["webhook.test", "Test event"],
]);

export function eventLabel(type: string): string {
	return EVENT_LABEL.get(type) ?? type;
}

/** "hooks.example.com/hephon": enough to tell endpoints apart in a narrow column. */
export function shortUrl(url: string): string {
	try {
		const { host, pathname } = new URL(url);
		return pathname === "/" ? host : `${host}${pathname}`;
	} catch {
		return url;
	}
}

export function eventsSummary(eventTypes: string[]): string {
	if (eventTypes.length === 0) {
		return "All events";
	}
	if (eventTypes.length === 1) {
		return eventLabel(eventTypes[0] ?? "");
	}
	return `${eventTypes.length} events`;
}

export const DISABLED_REASON: Record<string, string> = {
	gone: "Your server answered 410 Gone",
	failing: "Every delivery failed for 5 days",
};
